import { useEffect } from "react";
import { log } from "@qgiv/core-js";
import {
    getMaxNumberOfHandshakeAttempts,
    sendHandshakeToPaymentIframe,
} from "./helpers";

// -------------------------------------------------------------------------
// TODO:
// Once the Payment Iframe is stable in production instead of removing the
// logs completely we should comment them out. That way we do not clutter
// up everyone's logs when they are developing, however we are able to
// quickly re-implement these logs when attempting to debug any issues
// related to the payment iframe.
// -------------------------------------------------------------------------
const secondsToPoll = 60;
const pollingInterval = 300;

const maxNumberOfHandshakeAttempts = getMaxNumberOfHandshakeAttempts(
    secondsToPoll,
    pollingInterval,
);

// Because the callback that is passed to setInterval() is added to the window
// every time it is re-run it is re-run outside of the scope of this hook. This
// means that we cannot rely on state to monitor the number of time that we
// attempted to poll the payment iframe. Instead, by defining this variable
// here it allows us to keep track of this data every time a new attempt is
// made to poll the payment iframe while also allowing us to replace old
// polling data wit new polling data every time the payment iframe is removed
// and then re-added to the DOM.
let paymentIframePoll;

/**
 *
 * @param {object} props
 * @param {boolean} props.hasReceivedHandshakeFromPaymentIframe
 * @param {Function} props.setHasExceededNumberOfHandshakeAttempts
 * @param {string} props.urlOfPaymentIframe
 * @returns {null}
 */
const usePollPaymentIframeForHandshake = ({
    hasReceivedHandshakeFromPaymentIframe,
    setHasExceededNumberOfHandshakeAttempts,
    urlOfPaymentIframe,
}) => {
    /**
     * @description We cannot use the hasReceivedHandshakeFromPaymentIframe
     * inside of this function as the cleanup logic in React is not just called
     * when a component dismounts, but instead it is called every time a component
     * re-renders. So including this flag here would actually stop polling the
     * payment iframe immediately after the hook is first rendered.
     * @returns {void}
     */
    const stopPollingPaymentIframeForHandshake = () => {
        if (paymentIframePoll.hasStarted) {
            log("HOST APPLICATION: STOP POLL");
            log({
                hasStarted: paymentIframePoll.hasStarted,
                intervalId: paymentIframePoll.intervalId,
                numberOfAttempts: paymentIframePoll.numberOfAttempts,
            });
            log("------------------------------------------");

            paymentIframePoll.hasStarted = false;
            clearInterval(paymentIframePoll.intervalId);
        }
    };

    /**
     * @description Need to be able to remove the logic that continuously
     * polls the payment iframe if a handshake is received or the entire
     * component is dismounted.
     * @returns {void}
     */
    const startPollingPaymentIframeForHandshake = () => {
        log("------------------------------------------");
        log("HOST APPLICATION: INITIATE POLL");

        // Because old polling data may exist if the payment iframe was
        // previously added and then removed from the DOM we need to create a
        // fresh set of polling data for us to work with.
        paymentIframePoll = {
            hasStarted: true,
            intervalId: 0,
            numberOfAttempts: 0,
        };

        paymentIframePoll.intervalId = setInterval(() => {
            if (
                paymentIframePoll.numberOfAttempts <
                maxNumberOfHandshakeAttempts
            ) {
                paymentIframePoll.numberOfAttempts++;
                log(
                    "HOST APPLICATION: SEND POST MESSAGE FROM EXTERNAL APP WITH HANDSHAKE EVENT",
                );
                log({
                    hasStarted: paymentIframePoll.hasStarted,
                    intervalId: paymentIframePoll.intervalId,
                    numberOfAttempts: paymentIframePoll.numberOfAttempts,
                });
                sendHandshakeToPaymentIframe(urlOfPaymentIframe);
            } else {
                setHasExceededNumberOfHandshakeAttempts(true);
                stopPollingPaymentIframeForHandshake();
            }
        }, pollingInterval);
    };

    /**
     * @description Establishes a connection with the payment iframe that
     * allows the payment iframe to freely send post messages back to the host
     * application in a way that prevents CORS issues on custom domains.
     * @returns {void}
     */
    useEffect(() => {
        if (!hasReceivedHandshakeFromPaymentIframe) {
            startPollingPaymentIframeForHandshake();
        } else {
            stopPollingPaymentIframeForHandshake();
        }

        return () => {
            stopPollingPaymentIframeForHandshake();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasReceivedHandshakeFromPaymentIframe]);

    return null;
};

export default usePollPaymentIframeForHandshake;
